import * as THREE from 'three';
import {
  ICozumGeometri,
  IGostergeAyar,
  IGostergeSonuc,
  IGostergeMotoru,
  IDokumBolge,
  IDokumNotSonuc,
  GostergeTipi,
  DokumNotu,
  GOSTERGE_BASLIK,
} from './IDokumKontrol';

interface IKumeAday {
  tip: GostergeTipi;
  vertex: number;
  agirlik: number;
}

/**
 * Döküm gösterge motoru — saf, deterministik analiz.
 * Tüm göstergeler tek bir per-vertex kalınlık alanı + komşuluk grafiği üzerinden türetilir.
 */
export class FikoDokumGostergeler implements IGostergeMotoru {
  private komsuluk: number[][] = [];
  private vertexSayisi = 0;
  /** Sıcak nokta için adaptif kalınlık eşiği (parçanın üst-yüzdeliği, mm) */
  private sicakEsikMm = 0;
  /** Geçerli (karşı duvara isabet eden) kalınlık üst sınırı (no-hit ayıklama) */
  private maxGecerliMm = Infinity;

  hesapla(geo: ICozumGeometri, kalinlikAlani: Float32Array, ayar: IGostergeAyar): IGostergeSonuc {
    this.vertexSayisi = geo.vertexSayisi;
    this.komsulukKur(geo.indices, geo.vertexSayisi, geo.positions, geo.bbox);
    this.kalinlikEsikleriHesapla(geo, kalinlikAlani);

    const inceAdaylar = this.inceCidarTara(kalinlikAlani, ayar);
    const sicakAdaylar = this.sicakNoktaTara(kalinlikAlani, ayar);
    const kesitAdaylar = this.aniKesitTara(kalinlikAlani, ayar);
    const havaAdaylar = geo.tavanMesafe ? this.hapsolanHavaTara(geo, kalinlikAlani) : [];

    const bolgeler: IDokumBolge[] = [];
    let sira = 0;
    sira = this.adaylariKumele(inceAdaylar, geo, kalinlikAlani, ayar, bolgeler, sira);
    sira = this.adaylariKumele(sicakAdaylar, geo, kalinlikAlani, ayar, bolgeler, sira);
    sira = this.adaylariKumele(kesitAdaylar, geo, kalinlikAlani, ayar, bolgeler, sira);
    this.adaylariKumele(havaAdaylar, geo, kalinlikAlani, ayar, bolgeler, sira);

    let min = Infinity, max = -Infinity, toplam = 0, sayac = 0;
    for (let i = 0; i < kalinlikAlani.length; i++) {
      const k = kalinlikAlani[i];
      if (!isFinite(k) || k <= 0) continue;
      if (k < min) min = k;
      if (k > max) max = k;
      toplam += k;
      sayac++;
    }

    return {
      bolgeler,
      kalinlikAlani,
      minKalinlik: sayac ? min : 0,
      maxKalinlik: sayac ? max : 0,
      ortKalinlik: sayac ? toplam / sayac : 0,
    };
  }

  notHesapla(sonuc: IGostergeSonuc): IDokumNotSonuc {
    let ceza = 0;
    let kritikSayi = 0;
    let cozulen = 0;

    for (const b of sonuc.bolgeler) {
      if (b.cozuldu) { cozulen++; continue; } // yollukla giderilen bölge cezasız
      const tipCarpan =
        b.tip === 'sicak_nokta' ? 18 :
        b.tip === 'ince_cidar' ? 14 :
        b.tip === 'hapsolan_hava' ? 10 : 8;
      ceza += tipCarpan * (0.4 + 0.6 * b.siddet);
      if (b.siddet > 0.7) kritikSayi++;
    }

    const puan = Math.max(0, Math.min(100, Math.round(100 - ceza)));
    const not = this.notVer(puan);
    const ozet = this.ozetKur(sonuc.bolgeler, kritikSayi, puan, cozulen);

    return {
      puan,
      not,
      kritikSayi,
      toplamUyari: sonuc.bolgeler.length - cozulen, // çözülenler açık uyarı sayılmaz
      ozet,
    };
  }

  private komsulukKur(indices: Uint32Array, vertexSayisi: number, positions?: Float32Array, bbox?: any): void {
    const setler: Set<number>[] = new Array(vertexSayisi);
    for (let i = 0; i < vertexSayisi; i++) setler[i] = new Set<number>();

    // 1) Üçgen kenar komşuluğu
    for (let t = 0; t < indices.length; t += 3) {
      const a = indices[t], b = indices[t + 1], c = indices[t + 2];
      setler[a].add(b); setler[a].add(c);
      setler[b].add(a); setler[b].add(c);
      setler[c].add(a); setler[c].add(b);
    }

    // 2) Pozisyon kaynaklaması: STEP import'u çoğu zaman vertex'leri kaynaklamaz
    //    (her üçgenin kendi vertex'leri). Aynı konumdaki kopuk vertex'leri
    //    birbirine bağla → yüzey sürekli olur, bölgeler oluşabilir.
    if (positions && bbox) {
      const boyut = bbox.max.clone().sub(bbox.min);
      const kose = Math.hypot(boyut.x, boyut.y, boyut.z) || 1;
      const hucre = Math.max(kose * 1e-5, 1e-4); // kaynak toleransı
      const harita = new Map<string, number[]>();
      for (let i = 0; i < vertexSayisi; i++) {
        const x = Math.round(positions[i * 3] / hucre);
        const y = Math.round(positions[i * 3 + 1] / hucre);
        const z = Math.round(positions[i * 3 + 2] / hucre);
        const key = x + '_' + y + '_' + z;
        let g = harita.get(key);
        if (!g) { g = []; harita.set(key, g); }
        g.push(i);
      }
      for (const grup of harita.values()) {
        if (grup.length < 2) continue;
        for (let p = 0; p < grup.length; p++) {
          for (let q = 0; q < grup.length; q++) {
            if (p !== q) setler[grup[p]].add(grup[q]);
          }
        }
      }
    }

    this.komsuluk = new Array(vertexSayisi);
    for (let i = 0; i < vertexSayisi; i++) this.komsuluk[i] = Array.from(setler[i]);
  }

  /**
   * Adaptif eşikler: parçanın kalınlık dağılımına göre sıcak-nokta eşiği.
   * Karşı duvara isabet etmeyen (≈bbox) değerler ayıklanır, kalan geçerli
   * kalınlıkların üst %15'i "kalın bölge = sıcak nokta" kabul edilir.
   */
  private kalinlikEsikleriHesapla(geo: ICozumGeometri, kalinlik: Float32Array): void {
    const boyut = geo.bbox.max.clone().sub(geo.bbox.min);
    const kose = Math.hypot(boyut.x, boyut.y, boyut.z);
    this.maxGecerliMm = kose * 0.999;

    const gecerli: number[] = [];
    for (let i = 0; i < this.vertexSayisi; i++) {
      const k = kalinlik[i];
      if (isFinite(k) && k > 1e-4 && k < this.maxGecerliMm) gecerli.push(k);
    }
    if (gecerli.length < 16) { this.sicakEsikMm = Infinity; return; }
    gecerli.sort((a, b) => a - b);
    this.sicakEsikMm = gecerli[Math.floor(gecerli.length * 0.85)];
  }

  private inceCidarTara(kalinlik: Float32Array, ayar: IGostergeAyar): IKumeAday[] {
    const sonuc: IKumeAday[] = [];
    for (let i = 0; i < this.vertexSayisi; i++) {
      const k = kalinlik[i];
      if (!isFinite(k) || k <= 0) continue;
      if (k < ayar.minCidarMm) {
        const agirlik = Math.min(1, (ayar.minCidarMm - k) / Math.max(ayar.minCidarMm, 1e-6));
        sonuc.push({ tip: 'ince_cidar', vertex: i, agirlik });
      }
    }
    return sonuc;
  }

  /**
   * Sıcak nokta = en kalın (geç katılaşan) bölgeler. Mutlak modül eşiği VE
   * parçanın üst-yüzdelik adaptif eşiğinden hangisi düşükse o kullanılır —
   * böylece hem ince cidarlı parçada (mutlak modül) hem dolu blokta (yüzdelik)
   * anlamlı, bölge-tabanlı (izole nokta değil) sonuç verir. No-hit ayıklanır.
   */
  private sicakNoktaTara(kalinlik: Float32Array, ayar: IGostergeAyar): IKumeAday[] {
    const sonuc: IKumeAday[] = [];
    const mutlakEsikMm = ayar.sicakNoktaModulCm * 20; // modül(cm) → kalınlık(mm): k = modül*2*10
    const esik = Math.min(mutlakEsikMm, this.sicakEsikMm);
    if (!isFinite(esik)) return sonuc;

    for (let i = 0; i < this.vertexSayisi; i++) {
      const k = kalinlik[i];
      if (!isFinite(k) || k <= esik || k >= this.maxGecerliMm) continue;
      const pay = (k - esik) / Math.max(this.maxGecerliMm - esik, 1e-6);
      sonuc.push({ tip: 'sicak_nokta', vertex: i, agirlik: Math.min(1, 0.3 + 0.7 * pay) });
    }
    return sonuc;
  }

  private aniKesitTara(kalinlik: Float32Array, ayar: IGostergeAyar): IKumeAday[] {
    const sonuc: IKumeAday[] = [];
    const minGecerli = 0.5; // mm — altı yüzey gürültüsü (0.01 gibi), oran patlatır
    const gecerliMi = (v: number) => isFinite(v) && v >= minGecerli && v < this.maxGecerliMm;
    for (let i = 0; i < this.vertexSayisi; i++) {
      const k = kalinlik[i];
      if (!gecerliMi(k)) continue;
      const komsular = this.komsuluk[i];
      let enYuksekOran = 0;
      for (let j = 0; j < komsular.length; j++) {
        const nk = kalinlik[komsular[j]];
        if (!gecerliMi(nk)) continue;       // no-hit / gürültü komşuyu sayma
        const oran = k >= nk ? k / nk : nk / k;
        if (oran > enYuksekOran) enYuksekOran = oran;
      }
      if (enYuksekOran > ayar.kesitOraniEsik) {
        const oranK = Math.min(enYuksekOran, 20); // aşırı değerleri kırp
        const agirlik = Math.min(1, (oranK - ayar.kesitOraniEsik) / Math.max(ayar.kesitOraniEsik, 1e-6));
        sonuc.push({ tip: 'ani_kesit', vertex: i, agirlik });
      }
    }
    return sonuc;
  }

  /**
   * Hapsolan hava: GPU'da hesaplanmış "tavan mesafesi" alanını kullanır.
   * geo.tavanMesafe[i] = i. vertex'ten +Y (yukarı) yöndeki ilk yüzeye mesafe.
   * Yukarı bakan (normal.y>0.5) bir vertex'in üstünde sonlu mesafede tavan varsa
   * → o cebe hava hapsolur, hava deliği gerekir.
   */
  private hapsolanHavaTara(geo: ICozumGeometri, _kalinlik: Float32Array): IKumeAday[] {
    const sonuc: IKumeAday[] = [];
    const tavan = geo.tavanMesafe!;
    const normals = geo.normals;
    const up = geo.yukariYon ?? { x: 0, y: 1, z: 0 } as any;
    const ux = up.x, uy = up.y, uz = up.z;
    const boyut = geo.bbox.max.clone().sub(geo.bbox.min);
    const yukYukseklik = Math.abs(boyut.x * ux) + Math.abs(boyut.y * uy) + Math.abs(boyut.z * uz); // yukarı eksenindeki uzanım
    // Yakın tavan = gerçek cep. Uzak/no-hit (≈bbox) = açık gökyüzü, hapis değil.
    const ustSinir = Math.min(yukYukseklik * 0.5, this.maxGecerliMm * 0.95);
    const altSinir = yukYukseklik * 0.01;

    for (let i = 0; i < this.vertexSayisi; i++) {
      const ny0 = normals[i * 3] * ux + normals[i * 3 + 1] * uy + normals[i * 3 + 2] * uz; // normal·yukarı
      if (ny0 <= 0.5) continue;

      const d = tavan[i];
      if (d > altSinir && d < ustSinir) {
        const kapaliklik = 1 - d / ustSinir; // tavan ne kadar yakınsa o kadar kritik
        sonuc.push({ tip: 'hapsolan_hava', vertex: i, agirlik: Math.min(1, ny0 * (0.4 + 0.6 * kapaliklik)) });
      }
    }
    return sonuc;
  }

  private adaylariKumele(
    adaylar: IKumeAday[],
    geo: ICozumGeometri,
    kalinlik: Float32Array,
    ayar: IGostergeAyar,
    cikti: IDokumBolge[],
    siraBaslangic: number,
  ): number {
    if (adaylar.length === 0) return siraBaslangic;

    const agirlikHar = new Map<number, number>();
    const adayKume = new Set<number>();
    for (const a of adaylar) {
      adayKume.add(a.vertex);
      agirlikHar.set(a.vertex, a.agirlik);
    }

    const ziyaret = new Set<number>();
    const positions = geo.positions;
    const merkez = new THREE.Vector3();
    let sira = siraBaslangic;
    const tip = adaylar[0].tip;

    for (const baslangic of adayKume) {
      if (ziyaret.has(baslangic)) continue;

      const kumeVertexler: number[] = [];
      const yigin: number[] = [baslangic];
      ziyaret.add(baslangic);

      while (yigin.length) {
        const v = yigin.pop()!;
        kumeVertexler.push(v);
        const komsular = this.komsuluk[v];
        for (let j = 0; j < komsular.length; j++) {
          const n = komsular[j];
          if (adayKume.has(n) && !ziyaret.has(n)) {
            ziyaret.add(n);
            yigin.push(n);
          }
        }
      }

      if (kumeVertexler.length < ayar.minBolgeVertex) continue;

      merkez.set(0, 0, 0);
      let agirlikTop = 0, degerTop = 0;
      for (let m = 0; m < kumeVertexler.length; m++) {
        const v = kumeVertexler[m];
        merkez.x += positions[v * 3 + 0];
        merkez.y += positions[v * 3 + 1];
        merkez.z += positions[v * 3 + 2];
        agirlikTop += agirlikHar.get(v) || 0;
        degerTop += kalinlik[v];
      }
      const n = kumeVertexler.length;
      merkez.multiplyScalar(1 / n);

      const siddet = Math.min(1, agirlikTop / n);
      const ortKalinlik = degerTop / n;
      const { deger, birim } = this.bolgeDeger(tip, ortKalinlik, kalinlik, kumeVertexler);

      cikti.push({
        id: `${tip}-${sira}`,
        tip,
        merkez: merkez.clone(),
        vertexIndisleri: kumeVertexler,
        siddet,
        deger,
        birim,
        baslik: GOSTERGE_BASLIK[tip],
        aciklama: this.aciklamaKur(tip, deger, birim, n),
        oneri: this.oneriKur(tip, deger, ayar),
      });
      sira++;
    }
    return sira;
  }

  private bolgeDeger(
    tip: GostergeTipi,
    ortKalinlik: number,
    kalinlik: Float32Array,
    vertexler: number[],
  ): { deger: number; birim: string } {
    if (tip === 'sicak_nokta') {
      return { deger: this.yuvarla(ortKalinlik / 2 / 10), birim: 'cm' };
    }
    if (tip === 'ani_kesit') {
      let enYuksek = 0;
      for (let m = 0; m < vertexler.length; m++) {
        const v = vertexler[m];
        const k = kalinlik[v];
        const komsular = this.komsuluk[v];
        for (let j = 0; j < komsular.length; j++) {
          const nk = kalinlik[komsular[j]];
          if (!isFinite(nk) || nk <= 0 || !isFinite(k) || k <= 0) continue;
          const oran = k >= nk ? k / nk : nk / k;
          if (oran > enYuksek) enYuksek = oran;
        }
      }
      return { deger: this.yuvarla(enYuksek), birim: 'x' };
    }
    return { deger: this.yuvarla(ortKalinlik), birim: 'mm' };
  }

  private aciklamaKur(tip: GostergeTipi, deger: number, birim: string, vertexSayisi: number): string {
    switch (tip) {
      case 'ince_cidar':
        return `Ortalama ${deger} ${birim} cidar, ${vertexSayisi} nokta. Dolum sırasında soğuk birleşme riski.`;
      case 'sicak_nokta':
        return `Yerel modül ${deger} ${birim}. Geç katılaşan kütle, çekme boşluğu riski.`;
      case 'ani_kesit':
        return `Komşu kesit oranı ${deger}${birim}. Gerilme yığılması ve sıcak yırtılma riski.`;
      case 'hapsolan_hava':
        return `Üst yüzeyde kapanan cep (${vertexSayisi} nokta). Hava/gaz hapsi riski.`;
      default:
        return '';
    }
  }

  private oneriKur(tip: GostergeTipi, deger: number, ayar: IGostergeAyar): string {
    switch (tip) {
      case 'ince_cidar':
        return `Cidarı ${this.yuvarla(ayar.minCidarMm)}mm'ye çıkar.`;
      case 'sicak_nokta':
        return 'Besleyici ekle veya soğutucu yerleştir.';
      case 'ani_kesit':
        return 'Radüs ekle, kesit geçişini yumuşat.';
      case 'hapsolan_hava':
        return 'Hava deliği aç.';
      default:
        return '';
    }
  }

  private notVer(puan: number): DokumNotu {
    if (puan >= 85) return 'A';
    if (puan >= 70) return 'B';
    if (puan >= 55) return 'C';
    if (puan >= 40) return 'D';
    return 'F';
  }

  private ozetKur(bolgeler: IDokumBolge[], kritikSayi: number, puan: number, cozulen = 0): string {
    if (bolgeler.length === 0) return 'Sorun tespit edilmedi, döküme uygun.';

    const acik = bolgeler.filter(b => !b.cozuldu);
    const cozumEk = cozulen > 0 ? ` (${cozulen} yollukla çözüldü)` : '';
    if (acik.length === 0) return `Tüm uyarılar yollukla giderildi${cozumEk}, döküme uygun.`;

    const sayim: Record<string, number> = {};
    for (const b of acik) sayim[b.tip] = (sayim[b.tip] || 0) + 1;

    const parcalar: string[] = [];
    if (sayim['sicak_nokta']) parcalar.push(`${sayim['sicak_nokta']} sıcak nokta`);
    if (sayim['ince_cidar']) parcalar.push(`${sayim['ince_cidar']} ince cidar`);
    if (sayim['ani_kesit']) parcalar.push(`${sayim['ani_kesit']} ani kesit`);
    if (sayim['hapsolan_hava']) parcalar.push(`${sayim['hapsolan_hava']} hapsolan hava`);

    const onek = kritikSayi > 0 ? `Riskli (${kritikSayi} kritik): ` : 'Uyarı: ';
    return `${onek}${parcalar.join(', ')}${cozumEk}.`;
  }

  private yuvarla(deger: number): number {
    return Math.round(deger * 100) / 100;
  }
}
